import { Tag } from "antd";
import { get } from "lodash";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import {
  declaredMappings,
  formatFrozenValue,
  mappingRow,
  parseFrozenValue,
  setMappingRow,
  unusedMappings,
} from "./connectorMapping";

const MappingHead = ({ mapping, labels }) => (
  <div className="canvas-connector-map__head">
    <span className="canvas-connector-map__label">{mapping.label}</span>
    {mapping.required ? <Tag color="orange">{labels.REQUIRED}</Tag> : null}
  </div>
);

const MappingHint = ({ mapping }) =>
  mapping.hint ? (
    <div className="canvas-editor__meta-xs">{mapping.hint}</div>
  ) : null;

const InputMapping = ({ mapping, row, labels, onChange }) => (
  <div className="canvas-connector-map">
    <MappingHead mapping={mapping} labels={labels} />
    <div className="canvas-connector-map__row">
      <Input
        placeholder={labels.MAP_FROM}
        value={get(row, "from", "")}
        onChange={(e) => onChange({ from: e.target.value })}
      />
      <Input
        placeholder={labels.MAP_VALUE}
        value={formatFrozenValue(get(row, "value"))}
        onChange={(e) => onChange({ value: parseFrozenValue(e.target.value) })}
      />
    </div>
    <MappingHint mapping={mapping} />
  </div>
);

const OutputMapping = ({ mapping, row, labels, onChange }) => (
  <div className="canvas-connector-map">
    <MappingHead mapping={mapping} labels={labels} />
    <div className="canvas-connector-map__row">
      <Input
        placeholder={labels.MAP_TO}
        value={get(row, "to", "")}
        onChange={(e) => onChange({ to: e.target.value })}
      />
    </div>
    <MappingHint mapping={mapping} />
  </div>
);

const Unused = ({ rows, labels, onRemove }) =>
  rows.length ? (
    <div className="canvas-connector-map__unused">
      <span>
        {labels.UNUSED}{" "}
        {rows
          .map(({ name }) => name)
          .filter(Boolean)
          .join(", ")}
      </span>
      <Button type="primary--link" onClick={onRemove}>
        {labels.REMOVE_UNUSED}
      </Button>
    </div>
  ) : null;

const ConnectorMappings = ({ connector, config, labels, onChange }) => {
  const { operations, operation, inputs, outputs } = declaredMappings(
    connector,
    config,
  );
  const inputRows = get(config, "inputs") || [];
  const outputRows = get(config, "outputs") || [];
  const unused = [
    ...unusedMappings(inputRows, inputs),
    ...unusedMappings(outputRows, outputs),
  ];
  const waitsForOperation = operations.length > 0 && !operation;

  return (
    <>
      {operations.length ? (
        <Select
          label={labels.OPERATION}
          placeholder={labels.OPERATION_PLACEHOLDER}
          options={operations.map(({ key, label }) => ({ value: key, label }))}
          value={get(config, "operation") || undefined}
          onChange={(next) => onChange({ operation: next })}
        />
      ) : null}
      {inputs.length ? (
        <>
          <div className="canvas-editor__card-title">{labels.INPUTS}</div>
          {inputs.map((mapping) => (
            <InputMapping
              key={mapping.name}
              mapping={mapping}
              row={mappingRow(inputRows, mapping)}
              labels={labels}
              onChange={(patch) =>
                onChange({ inputs: setMappingRow(inputRows, mapping, patch) })
              }
            />
          ))}
          <div className="canvas-editor__meta-xs">{labels.MAP_VALUE_HINT}</div>
        </>
      ) : null}
      {outputs.length ? (
        <>
          <div className="canvas-editor__card-title">{labels.OUTPUTS}</div>
          {outputs.map((mapping) => (
            <OutputMapping
              key={mapping.name}
              mapping={mapping}
              row={mappingRow(outputRows, mapping)}
              labels={labels}
              onChange={(patch) =>
                onChange({
                  outputs: setMappingRow(outputRows, mapping, patch),
                })
              }
            />
          ))}
        </>
      ) : null}
      {!inputs.length && !outputs.length && !waitsForOperation ? (
        <p className="canvas-tab__hint">{labels.NO_MAPPING}</p>
      ) : null}
      <Unused
        rows={unused}
        labels={labels}
        onRemove={() =>
          onChange({
            inputs: inputRows.filter((row) => !unused.includes(row)),
            outputs: outputRows.filter((row) => !unused.includes(row)),
          })
        }
      />
    </>
  );
};

export default ConnectorMappings;
